import { PropsWithChildren, useState } from 'react';
import { useDesktopTopNav } from '../../common/Experiments/DesktopTopNavExperiment';
import { useSearchFilters } from '../../common/components/SearchHeader/filters/useSearchFilters';
import {
  COVID_19_FLU_VACCINATIONS,
  SPECIALTY_DENTIST,
  SPECIALTY_GENERAL_PRACTICE,
} from '../../common/components/SearchHeader/types/FilterChoice';
import { useStatefulScrollLock } from '../../common/hooks/useStatefulScrollLock';
import { FreeTextSearch } from '../../common/schema/hannibal/types.generated';
import { useMobile } from '../../common/services/screenSize/ScreenSizeService';
import { useOnScroll } from '../../common/util/useOnScroll';
import { Container, PillRow, TextDescription, MapSearchLink } from './FilterContainer.styles';
import { BulkBillingFilter } from './filters/BulkBillingFilter';
import { DateFilter } from './filters/DateFilter/DateFilter';
import { GenderFilter } from './filters/GenderFilter';
import { HicapsFilter } from './filters/HicapsFilter';
import { InsuranceFilter } from './filters/InsuranceFilter/InsuranceFilter';
import { LocationFilter } from './filters/LocationFilter/LocationFilter';
import { MoreFilters } from './filters/MoreFilters/MoreFilters';
import { OnlineAppointmentsFilter } from './filters/OnlineAppointmentsFilter';
import { PricingFilter } from './filters/PricingFilter';
import { ResetAllButton } from './filters/ResetAllButton';
import { SpecialOfferFilter } from './filters/SpecialOfferFilter';
import { SpecialtyFilter } from './filters/SpecialtyFilter/SpecialtyFilter';
import { ConsultationTypeFilter } from './filters/TelehealthFilter';
import { TimeFilter } from './filters/TimeFilter';
import { VaccineTypeFilter } from './filters/VaccineTypeFilter';
import { OverlayArea } from './overlay/OverlayArea';
import { TopLayer } from './styles';

type Props = PropsWithChildren<{
  searchFacets?: FreeTextSearch['searchProfiles']['facetsWithCounts'];
  loading?: boolean;
  toggleMapSearch?: () => void;
  mapSearchEnabled?: boolean;
}>;

export const FilterContainer = ({
  children,
  searchFacets,
  loading,
  toggleMapSearch,
  mapSearchEnabled,
}: Props) => {
  const [filters] = useSearchFilters();
  const isMobile = useMobile();
  const { locked } = useStatefulScrollLock();
  const onScroll = useOnScroll();
  const desktopTopNavExperiment = useDesktopTopNav();

  onScroll(({ y, previousY }) => {
    if (y < 80 || y < previousY || (desktopTopNavExperiment ? false : !isMobile)) {
      setFiltersVisible(true);
    } else {
      setFiltersVisible(false);
    }
  });

  const [filtersVisible, setFiltersVisible] = useState<boolean>(true);

  const { alwaysShowFilters, location, topic } = filters;

  /** If a location-based suggestion is chosen, we hide the associated pills */
  const shouldShowLocationFilter = alwaysShowFilters || !location;

  const shouldShowSpecialtyFilter =
    alwaysShowFilters || (!topic?.specialist && !topic?.bulkBillingGp);

  const isGp = topic?.bulkBillingGp || topic?.specialist === SPECIALTY_GENERAL_PRACTICE;
  const isDental = topic?.specialist === SPECIALTY_DENTIST;
  const isCovax = topic?.specialist === COVID_19_FLU_VACCINATIONS;

  const shouldShowInsuranceAndHiCaps = !isGp && !isCovax;
  const shouldShowBulkBilling = isGp && !isCovax;

  const { languagesName, governmentSchemes, paymentMethods, preferredHealthProviders, tags } =
    searchFacets ?? {};

  const areMoreFilters =
    loading ||
    Math.max(languagesName?.count ?? 0, paymentMethods?.count ?? 0, governmentSchemes?.count ?? 0) >
      0;

  return (
    <>
      <div className="branch-journeys-top"></div>
      {!isMobile && <OverlayArea />}

      <TopLayer
        visible={filtersVisible || locked}
        transition={!locked}
        experiment={desktopTopNavExperiment}
      >
        <Container>
          <PillRow>
            {!isMobile && <TextDescription>FILTER BY:</TextDescription>}
            {shouldShowSpecialtyFilter && <SpecialtyFilter />}
            {shouldShowLocationFilter && <LocationFilter />}
            <DateFilter />
            {isCovax && <VaccineTypeFilter />}
            <OnlineAppointmentsFilter />
            {isDental && (
              <>
                <PricingFilter />
                <SpecialOfferFilter />
              </>
            )}
            {shouldShowInsuranceAndHiCaps && (
              <InsuranceFilter preferredHealthProviderCount={preferredHealthProviders?.count} />
            )}
            {!isCovax && <ConsultationTypeFilter tags={tags?.values} />}
            {shouldShowInsuranceAndHiCaps && <HicapsFilter values={paymentMethods?.values} />}
            {shouldShowBulkBilling && <BulkBillingFilter />}
            <GenderFilter />
            <TimeFilter />
            {areMoreFilters && <MoreFilters />}
            <ResetAllButton />
            {mapSearchEnabled && (
              <MapSearchLink onClick={toggleMapSearch}>Map search</MapSearchLink>
            )}
          </PillRow>
        </Container>
      </TopLayer>

      {isMobile && <OverlayArea />}

      {children}
    </>
  );
};
